import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import './AdminApps.css'; // Reusing the same CSS for consistency

function AdminDashboard() {
  const { currentUser, isSuperAdmin } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalApps: 0,
    activeApps: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch stats on component mount
  useEffect(() => {
    if (isSuperAdmin) {
      fetchStats();
    }
  }, [isSuperAdmin]);

  // Fetch basic stats for the dashboard
  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch users count
      const usersResponse = await apiService.get('/users');
      const totalUsers = usersResponse.users?.length || 0;
      
      // Fetch apps count
      const appsResponse = await apiService.get('/apps');
      const apps = appsResponse.apps || [];
      const totalApps = apps.length;
      const activeApps = apps.filter(app => app.is_active).length;
      
      setStats({
        totalUsers,
        totalApps,
        activeApps
      });
      
      setError(null);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setError('Failed to load dashboard stats. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-container">
      <h1>Admin Dashboard</h1>
      
      {/* Admin navigation */}
      <div className="admin-nav mb-4">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <Link to="/admin" className="nav-link active">Dashboard</Link>
          </li>
          <li className="nav-item">
            <Link to="/admin/apps" className="nav-link">Apps</Link>
          </li>
          <li className="nav-item">
            <Link to="/admin/users" className="nav-link">Users</Link>
          </li>
          <li className="nav-item">
            <Link to="/admin/roles" className="nav-link">Roles</Link>
          </li>
        </ul>
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="text-center p-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading dashboard stats...</p>
        </div>
      ) : (
        <div className="admin-dashboard">
          {/* Stats cards */}
          <div className="admin-stats">
            <div className="admin-stat-card">
              <div className="admin-stat-icon">
                <i className="fas fa-users"></i>
              </div>
              <div className="admin-stat-content">
                <div className="admin-stat-value">{stats.totalUsers}</div>
                <div className="admin-stat-label">Total Users</div>
              </div>
            </div>
            
            <div className="admin-stat-card">
              <div className="admin-stat-icon">
                <i className="fas fa-cogs"></i>
              </div>
              <div className="admin-stat-content">
                <div className="admin-stat-value">{stats.totalApps}</div>
                <div className="admin-stat-label">Total Apps</div>
              </div>
            </div>
            
            <div className="admin-stat-card">
              <div className="admin-stat-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="admin-stat-content">
                <div className="admin-stat-value">{stats.activeApps}</div>
                <div className="admin-stat-label">Active Apps</div>
              </div>
            </div>
          </div>
          
          {/* Admin sections */}
          <div className="admin-sections">
            <div className="admin-section-card">
              <div className="admin-section-header">
                <i className="fas fa-cogs"></i>
                <h2>App Management</h2>
              </div>
              <p>Create, edit, and manage SaltMarsh Apps (tenants). Configure app settings, domains, and branding.</p>
              <Link to="/admin/apps" className="btn btn-primary">Manage Apps</Link>
            </div>
            
            <div className="admin-section-card">
              <div className="admin-section-header">
                <i className="fas fa-users-cog"></i>
                <h2>User Management</h2>
              </div>
              <p>Create and manage users, assign roles, and associate users with specific SaltMarsh Apps.</p>
              <Link to="/admin/users" className="btn btn-primary">Manage Users</Link>
            </div>
            
            <div className="admin-section-card">
              <div className="admin-section-header">
                <i className="fas fa-user-shield"></i>
                <h2>Role Management</h2>
              </div>
              <p>Create, edit, and manage roles. Define permissions and assign users to roles.</p>
              <Link to="/admin/roles" className="btn btn-primary">Manage Roles</Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard; 